import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Loader2, Save, Building2, Briefcase, MapPin, AlertTriangle,
  Droplets, Zap, BrickWall, Home, PaintBucket, Grid3X3, DoorOpen,
  Tractor, TreeDeciduous, Key, Snowflake, HelpCircle, Wrench,
  Thermometer, Hammer, MoreHorizontal
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { z } from 'zod';
import HeuresSupSettings from '@/components/settings/HeuresSupSettings';
import { useHeuresSupSettings } from '@/hooks/useHeuresSup';

// Schema de validation Zod pour les infos entreprise
const entrepriseInfoSchema = z.object({
  nom: z.string().min(1, "Le nom est requis").max(150, "150 caractères max"),
  siret: z.string().max(20, "20 caractères max").optional(),
  adresse: z.string().max(300, "300 caractères max").optional(),
  telephone: z.string().max(20, "20 caractères max").regex(/^[0-9\s+\-.()]*$/, "Format téléphone invalide").optional(),
});
import {
  CATEGORIES_METIERS,
  BENCHMARKS_METIERS,
  getBenchmarkMetier,
} from '@/data/benchmarkMetiers';
import type { HeuresSupSettings as HeuresSupSettingsType } from '@/types/heures-sup.types';

// Mapping des icônes Lucide par nom (clé = nom dans les données, valeur = composant)
const ICON_MAP: Record<string, LucideIcon> = {
  Droplets,
  Zap,
  Brick: BrickWall,
  Home,
  PaintBucket,
  Grid3x3: Grid3X3,
  DoorOpen,
  Tractor,
  TreeDeciduous,
  Building2,
  Key,
  Snowflake,
  HelpCircle,
  Wrench,
  Thermometer,
  Hammer,
  MoreHorizontal,
};

export default function EntrepriseSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingMetier, setSavingMetier] = useState(false);
  const [entreprise, setEntreprise] = useState<any>(null);
  const [nomEntreprise, setNomEntreprise] = useState('');
  const [siret, setSiret] = useState('');
  const [adresse, setAdresse] = useState('');
  const [telephone, setTelephone] = useState('');
  // États métier
  const [selectedCategorie, setSelectedCategorie] = useState<string>('');
  const [metierKey, setMetierKey] = useState<string>('autre');
  const [localisationType, setLocalisationType] = useState<'province' | 'metropole'>('province');
  const [initialMetierKey, setInitialMetierKey] = useState<string>('autre');
  const { toast } = useToast();

  // Hook pour les settings heures sup
  const {
    settings: heuresSupSettings,
    isLoading: heuresSupLoading,
    updateSettings: updateHeuresSupSettings,
  } = useHeuresSupSettings(entreprise?.id || null);

  // Filtrer les métiers par catégorie sélectionnée
  const metiersFiltres = useMemo(() => {
    if (!selectedCategorie) return [];
    return BENCHMARKS_METIERS.filter(m => m.categorie_key === selectedCategorie);
  }, [selectedCategorie]);

  // Trouver le métier sélectionné
  const metierSelectionne = useMemo(() => {
    return getBenchmarkMetier(metierKey);
  }, [metierKey]);

  // Vérifier si le métier a changé
  const metierHasChanged = metierKey !== initialMetierKey;

  useEffect(() => {
    const loadEntreprise = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data } = await supabase
        .from('entreprises')
        .select('*')
        .eq('proprietaire_user_id', user.id)
        .single();

      if (data) {
        setEntreprise(data);
        setNomEntreprise(data.nom || '');
        setSiret(data.siret || '');
        setAdresse(data.adresse || '');
        setTelephone(data.telephone || '');
        // Charger les données métier
        const currentMetierKey = data.metier_key || 'autre';
        setMetierKey(currentMetierKey);
        setInitialMetierKey(currentMetierKey);
        setLocalisationType(data.localisation_type || 'province');
        // Trouver la catégorie du métier actuel
        const currentMetier = getBenchmarkMetier(currentMetierKey);
        if (currentMetier) {
          setSelectedCategorie(currentMetier.categorie_key);
        }
      }
      setLoading(false);
    };
    loadEntreprise();
  }, []);

  const handleSave = async () => {
    if (!entreprise) return;

    // Validation Zod
    const validation = entrepriseInfoSchema.safeParse({
      nom: nomEntreprise.trim(),
      siret: siret.trim() || undefined,
      adresse: adresse.trim() || undefined,
      telephone: telephone.trim() || undefined,
    });

    if (!validation.success) {
      validation.error.issues.forEach(issue => {
        toast({ title: "Erreur", description: issue.message, variant: "destructive" });
      });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('entreprises')
        .update({
          nom: validation.data.nom,
          siret: validation.data.siret || siret,
          adresse: validation.data.adresse || adresse,
          telephone: validation.data.telephone || telephone,
        })
        .eq('id', entreprise.id);

      if (error) throw error;

      toast({
        title: 'Entreprise mise à jour',
        description: 'Les informations ont été enregistrées.',
      });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de mettre à jour',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Sauvegarder le métier et la localisation
  const handleSaveMetier = async () => {
    if (!entreprise) return;

    setSavingMetier(true);
    try {
      const { error } = await supabase
        .from('entreprises')
        .update({
          metier_key: metierKey,
          localisation_type: localisationType,
        })
        .eq('id', entreprise.id);

      if (error) throw error;

      setInitialMetierKey(metierKey);
      toast({
        title: 'Métier mis à jour',
        description: 'Vos seuils de rentabilité ont été adaptés à votre métier.',
      });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de mettre à jour le métier',
        variant: 'destructive',
      });
    } finally {
      setSavingMetier(false);
    }
  };

  // Gérer le changement de catégorie
  const handleCategorieChange = (categorieKey: string) => {
    setSelectedCategorie(categorieKey);
    // Réinitialiser le métier si on change de catégorie
    const metiers = BENCHMARKS_METIERS.filter(m => m.categorie_key === categorieKey);
    if (metiers.length > 0) {
      setMetierKey(metiers[0].metier_key);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!entreprise) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            Aucune entreprise associée à votre compte.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle>Informations entreprise</CardTitle>
              <CardDescription>
                Gérez les informations de votre entreprise
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nom-entreprise">Nom de l'entreprise</Label>
            <Input
              id="nom-entreprise"
              value={nomEntreprise}
              onChange={(e) => setNomEntreprise(e.target.value)}
              placeholder="Nom de votre entreprise"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="siret">SIRET</Label>
            <Input
              id="siret"
              value={siret}
              onChange={(e) => setSiret(e.target.value)}
              placeholder="123 456 789 00012"
            />
            <p className="text-xs text-muted-foreground mt-1">14 chiffres, visible sur votre Kbis. Optionnel.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adresse">Adresse</Label>
            <Input
              id="adresse"
              value={adresse}
              onChange={(e) => setAdresse(e.target.value)}
              placeholder="Adresse complète"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="telephone">Téléphone</Label>
            <Input
              id="telephone"
              value={telephone}
              onChange={(e) => setTelephone(e.target.value)}
              placeholder="01 23 45 67 89"
            />
          </div>

          <div className="pt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Section Métier & Benchmarks */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Briefcase className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle>Métier & Benchmarks</CardTitle>
              <CardDescription>
                Définissez votre métier pour personnaliser vos analyses de rentabilité
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Avertissement si le métier a changé */}
          {metierHasChanged && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Changer de métier modifiera vos seuils de rentabilité et vos analyses.
                Les nouveaux benchmarks seront appliqués à tous vos chantiers.
              </AlertDescription>
            </Alert>
          )}

          {/* Sélection de la catégorie */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Catégorie de métier
            </Label>
            <Select
              value={selectedCategorie}
              onValueChange={handleCategorieChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisissez une catégorie..." />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES_METIERS.map((cat) => {
                  const IconComponent = ICON_MAP[cat.icon] || HelpCircle;
                  return (
                    <SelectItem key={cat.categorie_key} value={cat.categorie_key}>
                      <span className="flex items-center gap-2">
                        <IconComponent className="w-4 h-4" />
                        <span>{cat.categorie_name}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Définit vos seuils de rentabilité et benchmarks sectoriels.</p>
          </div>

          {/* Sélection du métier */}
          {selectedCategorie && metiersFiltres.length > 0 && (
            <div className="space-y-2">
              <Label>Votre métier</Label>
              <Select
                value={metierKey}
                onValueChange={setMetierKey}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {metiersFiltres.map((metier) => (
                    <SelectItem key={metier.metier_key} value={metier.metier_key}>
                      <span className="flex items-center gap-2">
                        <span>{metier.metier_name}</span>
                        {metier.utilise_benchmarks_generiques && (
                          <Badge variant="secondary" className="text-xs">
                            Multi-travaux
                          </Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Localisation */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Localisation
            </Label>
            <RadioGroup
              value={localisationType}
              onValueChange={(value: 'province' | 'metropole') => setLocalisationType(value)}
              className="flex gap-4"
            >
              <div
                className={`flex-1 flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                  localisationType === 'province'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setLocalisationType('province')}
              >
                <RadioGroupItem value="province" id="settings-province" />
                <label htmlFor="settings-province" className="cursor-pointer">
                  <span className="font-medium">Province</span>
                  <p className="text-xs text-muted-foreground">
                    Hors région parisienne
                  </p>
                </label>
              </div>
              <div
                className={`flex-1 flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                  localisationType === 'metropole'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setLocalisationType('metropole')}
              >
                <RadioGroupItem value="metropole" id="settings-metropole" />
                <label htmlFor="settings-metropole" className="cursor-pointer">
                  <span className="font-medium">Métropole / IDF</span>
                  <p className="text-xs text-muted-foreground">
                    Paris et région parisienne
                  </p>
                </label>
              </div>
            </RadioGroup>
          </div>

          {/* Aperçu des benchmarks - masqué pour l'utilisateur, données utilisées en interne */}
          {metierSelectionne && (
            <div className="hidden">
              <p className="font-medium text-foreground">
                Benchmarks {metierSelectionne.metier_name}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Marge cible :</span>
                  <span className="font-medium">{metierSelectionne.marges.nette.cible}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Marge minimum :</span>
                  <span className="font-medium">{metierSelectionne.marges.nette.min}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tarif horaire :</span>
                  <span className="font-medium">
                    {metierSelectionne.tarifs_horaires[localisationType].min} - {metierSelectionne.tarifs_horaires[localisationType].max} €/h
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Coefficient :</span>
                  <span className="font-medium">{metierSelectionne.coefficient.standard}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground pt-2 border-t">
                Source : {metierSelectionne.source}
              </p>
            </div>
          )}

          {/* Bouton sauvegarder */}
          <div className="pt-2">
            <Button
              onClick={handleSaveMetier}
              disabled={savingMetier || !metierHasChanged}
              variant={metierHasChanged ? 'default' : 'outline'}
            >
              {savingMetier ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer le métier
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Section Heures Supplémentaires */}
      <HeuresSupSettings
        settings={heuresSupSettings}
        onSave={updateHeuresSupSettings}
        isLoading={heuresSupLoading}
      />
    </div>
  );
}
